import {
  validateOptionText,
  validateQuestionDescription,
  validateQuestionText,
} from './validation';
import { ANSWER_TYPES, CHOICE_BASED_TYPES, QuestionData } from '../types';

export interface QuestionContentValidationSlice {
  questionsWithInvalidText: QuestionData[];
  questionsWithInvalidDescription: QuestionData[];
  questionsWithoutAnswerType: QuestionData[];
  questionsWithEmptyOptions: QuestionData[];
  questionsWithInvalidOptions: QuestionData[];
  questionsWithMissingDatabaseField: QuestionData[];
  hasQuestionErrors: boolean;
}

export function computeQuestionContentValidation(
  trimmedQuestions: QuestionData[],
  isCoI?: boolean,
): QuestionContentValidationSlice {
  const questionsWithInvalidText = trimmedQuestions.filter(
    (q) => validateQuestionText(q.questionText, isCoI) !== null,
  );
  const questionsWithInvalidDescription = trimmedQuestions.filter(
    (q) => validateQuestionDescription(q.description) !== null,
  );
  const questionsWithoutAnswerType = trimmedQuestions.filter(
    (q) => !q.questionType,
  );

  const questionsWithEmptyOptions = trimmedQuestions.filter((q) => {
    if (!q.questionType || !CHOICE_BASED_TYPES.has(q.questionType)) {
      return false;
    }
    return (
      !q.options ||
      q.options.length === 0 ||
      q.options.some(
        (opt) => !opt.isOtherOption && (!opt.text || opt.text.trim() === ''),
      )
    );
  });

  const questionsWithInvalidOptions = trimmedQuestions.filter((q) => {
    if (!q.questionType || !CHOICE_BASED_TYPES.has(q.questionType)) {
      return false;
    }
    if (!q.options || q.options.length === 0) {
      return false;
    }
    const fieldName =
      q.questionType === ANSWER_TYPES.DATA_GRID
        ? 'Column header'
        : 'Option text';

    const hasBasicError = q.options.some(
      (opt) =>
        !opt.isOtherOption && validateOptionText(opt.text, fieldName) !== null,
    );

    const hasDuplicates = q.options.some((opt, index, allOptions) => {
      if (opt.isOtherOption) {
        return false;
      }
      const trimmedText = opt.text.trim().toLowerCase();
      return (
        trimmedText &&
        allOptions.some(
          (other, otherIndex) =>
            otherIndex !== index &&
            !other.isOtherOption &&
            other.text.trim().toLowerCase() === trimmedText,
        )
      );
    });

    return hasBasicError || hasDuplicates;
  });

  const questionsWithMissingDatabaseField = trimmedQuestions.filter(
    (q) =>
      q.questionType === ANSWER_TYPES.DATABASE_FIELD &&
      !q.databaseFieldOptions?.selectedFieldId,
  );

  const hasQuestionErrors =
    questionsWithInvalidText.length > 0 ||
    questionsWithInvalidDescription.length > 0 ||
    questionsWithoutAnswerType.length > 0 ||
    questionsWithEmptyOptions.length > 0 ||
    questionsWithInvalidOptions.length > 0 ||
    questionsWithMissingDatabaseField.length > 0;

  return {
    questionsWithInvalidText,
    questionsWithInvalidDescription,
    questionsWithoutAnswerType,
    questionsWithEmptyOptions,
    questionsWithInvalidOptions,
    questionsWithMissingDatabaseField,
    hasQuestionErrors,
  };
}
